import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import type { IQuerySingleQuestion } from "@/types/(my-space)/ai-assistant.interface";
import { dayLabel } from "./conversation-groups";
import { cn } from "@/utils/cn";
import { Check, ClipboardCopy } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useCopyToClipboard } from "usehooks-ts";
import { useLocale } from "@/hooks/useLocale";

// Hand-rolled rather than `typeof <one slice's hook>` — my-space and company
// generate this hook from separate createApi() calls with different tag-name
// literal unions baked into the type, so the other slice's (identical at
// runtime) hook fails a strict structural match against it.
type UseQuestionsBySessionHook = (arg: { sessionId: string }) => {
  data?: { pages: { data: IQuerySingleQuestion[] }[] };
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
};

/**
 * Copy affordance that confirms in place — the icon becomes a tick for a beat.
 * A toast for a copy is a notification for something the user just did on
 * purpose, in a corner away from where they did it.
 */
const CopyPromptButton = ({
  content,
  onCopy,
}: {
  content: string;
  onCopy: (value: string) => Promise<boolean>;
}) => {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const { t } = useTranslation(["common"]);
  const handleCopy = async () => {
    const ok = await onCopy(content);
    if (!ok) return;
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1400);
  };

  return (
    <button
      type="button"
      aria-label={
        copied
          ? t("common:ai_assistant.prompt_copied")
          : t("common:ai_assistant.copy_prompt")
      }
      title={
        copied ? t("common:buttons.copied") : t("common:ai_assistant.copy_prompt")
      }
      onClick={handleCopy}
      className={cn(
        "mt-1 grid size-6 shrink-0 place-items-center rounded-xs transition-opacity",
        copied
          ? "text-success opacity-100"
          : "text-muted-foreground hover:text-foreground opacity-0 group-hover/query:opacity-100 focus-visible:opacity-100 max-lg:opacity-100",
      )}
    >
      {copied ? <Check size={13} /> : <ClipboardCopy size={13} />}
      <span aria-live="polite" className="sr-only">
        {copied ? t("common:buttons.copied") : ""}
      </span>
    </button>
  );
};

interface ConversationQueriesProps {
  sessionId: string;
  panelId: string;
  useQuestionsBySession: UseQuestionsBySessionHook;
  /** Jump the transcript to this question. Matches the context's nullable,
   *  possibly-async handler. */
  onSelectQuery?:
    ((sessionId: string, questionId: number) => void | Promise<void>) | null;
}

/**
 * The prompts inside one conversation, revealed under its row.
 *
 * Mounting *is* the fetch: this component is only rendered while its row is
 * expanded, so no request is made for a chat nobody opened. RTK Query keeps the
 * result cached after collapse, so re-expanding is instant and free.
 */
export const ConversationQueries = ({
  sessionId,
  panelId,
  useQuestionsBySession,
  onSelectQuery,
}: ConversationQueriesProps) => {
  const { t } = useTranslation(["common", "my-space"]);
  const { formatDate } = useLocale();
  const [, copy] = useCopyToClipboard();
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useQuestionsBySession({ sessionId });

  const questions = data?.pages.flatMap((page) => page.data) ?? [];

  return (
    <div
      id={panelId}
      role="region"
      aria-label={t("common:a11y.prompts_in_this_conversation")}
      // The guide line ties the prompts to the conversation they belong to.
      className="border-border ms-6 flex flex-col gap-0.5 border-s py-1 ps-1.5"
    >
      {isLoading ? (
        [...Array(3)].map((_, i) => (
          <Skeleton
            key={i}
            className="mx-2 my-1.5 h-2.5"
            style={{ width: `${82 - i * 16}%` }}
          />
        ))
      ) : questions.length === 0 ? (
        <p className="text-muted-foreground mb-0! px-2 py-2 text-[11px]">
          {t("common:ai_assistant.no_prompts_in_chat")}
        </p>
      ) : (
        questions.map((question) => (
          <div
            key={question.id}
            className="group/query hover:bg-muted flex items-start gap-1 rounded-xs"
          >
            <button
              type="button"
              onClick={() => onSelectQuery?.(sessionId, question.id)}
              className="min-w-0 flex-1 px-2 py-1.5 text-start"
            >
              <span
                className={cn(
                  "text-foreground block text-[11.5px] leading-snug",
                  "line-clamp-2",
                )}
              >
                {question.content}
              </span>
              <span className="text-muted-foreground mt-0.5 block text-[10px]">
                {dayLabel(question.createdAt, t, formatDate)}
              </span>
            </button>

            <CopyPromptButton content={question.content} onCopy={copy} />
          </div>
        ))
      )}

      {hasNextPage && (
        <Button
          variant="ghost"
          size="xs"
          className="text-muted-foreground w-fit"
          isLoading={isFetchingNextPage}
          onClick={() => fetchNextPage()}
        >
          {t("common:ai_assistant.more_prompts")}
        </Button>
      )}
    </div>
  );
};
